// @flow
import { useParams } from 'react-router-dom';
import { getIn, useFormikContext } from 'formik';
import React, { useContext } from 'react';
import { resetFormValuesToInitialValuesUsingSetIn } from '@src/common-utils/utils';
import { useGlobalDiscountProvidedByStatus } from '@src/custom-hooks/useGlobalDiscountProvidedByStatus';
import { getIfAnyCommittedCapacityLineItemsAreConfigured } from '@components/committed-capacity/utils';

import {
  findVBDItemsLowerThanGlobalDiscount,
  getGlobalDiscountFromFormikValues,
} from '../../../components/custom-discounts/utils';
import {
  CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME,
  CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME_ORIGINAL,
  CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_IS_FROZEN_BACKEND_NAME,
  GLOBAL_DISCOUNT_BACKEND_NAME,
  isCommittedCapacityLiveForConnectOrTF,
} from '../../../constants';
import { UpdateFormButton } from '../../presentational/buttons/UpdateFormButton';
import { useUpdateEstimateMutation } from '../../../service-definitions/estimates';
import { toastError } from '../../presentational/notifications/utils';
import { EstimateContext } from '../../../contexts/EstimateContext';

import { getGlobalDiscountConfirmationModalBody } from './global-discount-confirmation-utils';

const getIfGlobalDiscountShouldBeDisabled = (readOnlyMode, formik) => {
  //  if we are in RO Mode, return true to disable it unconditionally
  if (readOnlyMode) {
    return true;
  }

  const currentValue = getIn(formik.values, CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME);

  // if the field has not been set yet, return true to disable it
  if (currentValue == null) {
    return true;
  }

  const initialValue = getIn(formik.initialValues, CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME);
  const isTheValueUnChanged = Number(currentValue) === Number(initialValue);

  // if the field has not been touched yet, return true to disable it
  if (isTheValueUnChanged) {
    return true;
  }

  // if the formik form is invalid, return true to disable it
  return !formik.isValid;
};

const performGlobalDiscountUpdate = async (formik, estimateId, updateEstimateDetails, estimate) => {
  const globalDiscount = getIn(formik.values, CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME);

  // Note: modified_fields is set directly to ['global_discount'] because:
  // 1. This component ONLY updates the global_discount field - it's an independent operation
  const payload = {
    global_discount: globalDiscount,
    estimate_version: estimate?.inputs?.estimate_version,
    modified_fields: [GLOBAL_DISCOUNT_BACKEND_NAME],
  };

  const { error } = await updateEstimateDetails({
    estimateId,
    payload,
  });

  if (error) {
    toastError(
      error,
      'There was an error in updating the discount, please contact #cloud-commitment-estimator channel!'
    );
  } else {
    await formik.setFieldValue(CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_IS_FROZEN_BACKEND_NAME, true);
    await formik.setFieldValue(
      CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME_ORIGINAL,
      globalDiscount
    );
  }
};

const getConfirmationConfigsForGlobalDiscountUpdate = (formik, isGlobalDiscountSystemGenerated) => {
  const confirmationConfigs = [];
  const globalDiscount = getGlobalDiscountFromFormikValues(formik.values);
  // Check if there are any VBD items with discount >= global discount
  const affectedMetrics = findVBDItemsLowerThanGlobalDiscount(formik.values, globalDiscount);

  const shouldShowGDConfirmation = affectedMetrics.length > 0 || isGlobalDiscountSystemGenerated;

  const shouldShowCCConfirmation =
    isCommittedCapacityLiveForConnectOrTF() &&
    getIfAnyCommittedCapacityLineItemsAreConfigured(formik.values);

  if (shouldShowGDConfirmation) {
    confirmationConfigs.push({
      header: 'Global Discount Change - Confirmation Required',
      body: getGlobalDiscountConfirmationModalBody(
        affectedMetrics,
        globalDiscount,
        isGlobalDiscountSystemGenerated
      ),
      okButtonText: 'Yes, I understand and want to proceed!',
      cancelButtonText: 'No, revert my changes!',
      fullScreen: true,
    });
  }

  if (shouldShowCCConfirmation) {
    confirmationConfigs.push({
      header: 'Committed Capacity Review Required',
      body: 'Changing the global discount may affect the effective discount for committed capacity. Please review your committed capacity configuration after saving. Non-admin rows will be auto-adjusted based on the new effective discount and admin rows will be untouched. However, a warning icon appears wherever the entered discount falls outside of the permitted range!',
      okButtonText: 'Yes, I understand!!',
      cancelButtonText: 'No, revert my changes!',
    });
  }

  return confirmationConfigs;
};

export const GlobalDiscountsUpdateButtonContainer = ({ readOnlyMode }) => {
  const { estimateId } = useParams();
  const estimate = useContext(EstimateContext);
  const { isGlobalDiscountSystemGenerated } = useGlobalDiscountProvidedByStatus();
  const formik = useFormikContext();
  const [updateEstimateDetails] = useUpdateEstimateMutation();

  const confirmationConfigs = getConfirmationConfigsForGlobalDiscountUpdate(
    formik,
    isGlobalDiscountSystemGenerated
  );

  const handleCancel = () => {
    resetFormValuesToInitialValuesUsingSetIn(
      formik.values,
      [{ backendFieldName: CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME }],
      formik.initialValues,
      formik.resetForm
    );
  };

  return (
    <UpdateFormButton
      confirmationConfigs={confirmationConfigs}
      disabled={getIfGlobalDiscountShouldBeDisabled(readOnlyMode, formik)}
      onCancel={handleCancel}
      onClick={() =>
        performGlobalDiscountUpdate(formik, estimateId, updateEstimateDetails, estimate)
      }
      shouldShowConfirmation={confirmationConfigs.length > 0}
    />
  );
};
